import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    IconButton,
    Tooltip,
    tableCellClasses,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArticleIcon from '@mui/icons-material/Article';
import Calculate from '@mui/icons-material/Calculate';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DataObjectIcon from '@mui/icons-material/DataObject';
import JavascriptIcon from '@mui/icons-material/Javascript';
import PaginacionTabla from '../elementos/eventos/filtros/SelectPaginaTabla';
import SelectNumeroCursos from '../elementos/eventos/filtros/SelectNumeroElementos';
import { fetchObtenerDocumento } from '../../peticiones_API/PeticionesDocumentos';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center !important',
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: 'rgba(0, 60, 102, 0.8)',
        color: theme.palette.common.white,
        padding: '10px',
        fontWeight: 'bold',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    textAlign: 'center !important',
    cursor: 'pointer',
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:nth-of-type(even)': {
        backgroundColor: 'transparent',
    },
    '&:hover': {
        backgroundColor: `${theme.palette.action.hover} !important`,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const TruncatedCell = styled(TableCell)(({ maxWidth = 200 }) => ({
    maxWidth: maxWidth,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const SortableHeaderCell = styled(StyledTableCell)(({ theme }) => ({
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
        backgroundColor: 'rgba(0, 60, 102, 0.9)',
    },
}));

const TablaDocumentos = ({ documentos, meta, onOrdenar, ordenColumna, orden }) => {
    const [modalAbierto, setModalAbierto] = useState(false);
    const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
    const [urlPreview, setUrlPreview] = useState(null);
    const [cargandoPreview, setCargandoPreview] = useState(false);
    const [errorPreview, setErrorPreview] = useState(false);
    
    const formatearTamanio = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const obtenerNombreArchivo = (ruta) => {
        return ruta.split('/').pop();
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '-';
        const date = new Date(fecha);
        return date.toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const descargarArchivo = async (e, idDocumento, nombre) => {
        e.stopPropagation();

        try {
            const response = await fetchObtenerDocumento(idDocumento);
            const blob = response.data;
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = nombre;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            alert('Error al descargar el archivo');
        }
    };

    const abrirPreview = async (documento) => {
        setDocumentoSeleccionado(documento);
        setModalAbierto(true);
        setCargandoPreview(true);
        setErrorPreview(false);
        setUrlPreview(null);

        try {
            const response = await fetchObtenerDocumento(documento.id);
            const blob = response.data;
            const blobUrl = window.URL.createObjectURL(blob);
            setUrlPreview(blobUrl);
            setCargandoPreview(false);
        } catch (error) {
            console.error('Error al cargar preview:', error);
            setErrorPreview(true);
            setCargandoPreview(false);
        }
    };

    const cerrarModal = () => {
        setModalAbierto(false);
        if (urlPreview) {
            window.URL.revokeObjectURL(urlPreview);
        }
        setUrlPreview(null);
        setDocumentoSeleccionado(null);
        setErrorPreview(false);
    };

    const obtenerTipoArchivo = (nombreArchivo) => {
        const extension = nombreArchivo.split('.').pop().toLowerCase();
        if (['pdf'].includes(extension)) return 'pdf';
        if (['doc', 'docx'].includes(extension)) return 'word';
        if (['xls', 'xlsx'].includes(extension)) return 'excel';
        if (['ppt', 'pptx'].includes(extension)) return 'powerpoint';
        // Imágenes
        if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico'].includes(extension)) return 'image';
        // Video
        if (['mp4', 'webm', 'mkv', 'avi', 'mov', 'wmv', 'flv'].includes(extension)) return 'video';
        // Audio
        if (['mp3', 'wav', 'wma', 'ogg', 'flac', 'aac', 'm4a'].includes(extension)) return 'audio';
        // Código y desarrollo
        if (['js', 'jsx', 'ts', 'tsx'].includes(extension)) return 'javascript';
        if (['html', 'htm', 'css', 'scss', 'sass', 'less'].includes(extension)) return 'web';
        if (['py', 'java', 'c', 'cpp', 'cs', 'php', 'rb', 'go', 'rs', 'swift', 'kt'].includes(extension)) return 'code';
        if (['json', 'xml', 'yaml', 'yml'].includes(extension)) return 'data';
        // Archivos comprimidos
        if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(extension)) return 'compressed';
        // Texto
        if (['txt', 'md', 'csv', 'log'].includes(extension)) return 'text';
        return 'other';
    };

    const obtenerIconoTipo = (tipo) => {
        switch (tipo) {
            case 'pdf':
                return <PictureAsPdfIcon style={{ color: '#d32f2f' }} />;
            case 'word':
                return <ArticleIcon style={{ color: '#2196f3' }} />;
            case 'excel':
                return <Calculate style={{ color: '#217346' }} />;
            case 'powerpoint':
                return <SlideshowIcon style={{ color: '#d24726' }} />;
            case 'image':
                return <ImageIcon style={{ color: '#1976d2' }} />;
            case 'video':
                return <VideoFileIcon style={{ color: '#9c27b0' }} />;
            case 'audio':
                return <AudioFileIcon style={{ color: '#f57c00' }} />;
            case 'javascript':
                return <JavascriptIcon style={{ color: '#f7df1e' }} />;
            case 'web':
                return <CodeIcon style={{ color: '#e34c26' }} />;
            case 'code':
                return <CodeIcon style={{ color: '#388e3c' }} />;
            case 'data':
                return <DataObjectIcon style={{ color: '#0288d1' }} />;
            case 'compressed':
                return <FolderZipIcon style={{ color: '#fbc02d' }} />;
            case 'text':
                return <DescriptionIcon style={{ color: '#616161' }} />;
            default:
                return <InsertDriveFileIcon style={{ color: '#757575' }} />;
        }
    };

    const renderSortIcon = (columna) => {
        if (ordenColumna !== columna) return null;
        return orden === 'ASC' ? (
            <ArrowUpwardIcon fontSize="small" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
        ) : (
            <ArrowDownwardIcon fontSize="small" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
        );
    };

    return (
        <>
            <Paper elevation={2}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell style={{ maxWidth: 80 }}>Tipo</StyledTableCell>
                                <SortableHeaderCell 
                                    align="left" 
                                    style={{ maxWidth: 300 }}
                                    onClick={() => onOrdenar && onOrdenar('ruta')}
                                >
                                    Nombre del Archivo
                                    {onOrdenar && renderSortIcon('ruta')}
                                </SortableHeaderCell>
                                <SortableHeaderCell 
                                    style={{ maxWidth: 100 }}
                                    onClick={() => onOrdenar && onOrdenar('tamaño')}
                                >
                                    Tamaño
                                    {onOrdenar && renderSortIcon('tamaño')}
                                </SortableHeaderCell>
                                <StyledTableCell style={{ maxWidth: 80 }}>Descargar</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {documentos.length === 0 ? (
                                <StyledTableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="textSecondary" style={{ padding: '20px' }}>
                                            No se encontraron documentos
                                        </Typography>
                                    </TableCell>
                                </StyledTableRow>
                            ) : (
                                documentos.map((doc) => {
                                    const nombreArchivo = obtenerNombreArchivo(doc.ruta);

                                    return (
                                        <StyledTableRow key={doc.id} onClick={() => abrirPreview(doc)}>
                                            <TableCell style={{ maxWidth: 80, textAlign: 'center' }}>
                                                <Tooltip title={obtenerTipoArchivo(nombreArchivo).toUpperCase()}>
                                                    <Box display="flex" justifyContent="center" alignItems="center">
                                                        {obtenerIconoTipo(obtenerTipoArchivo(nombreArchivo))}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TruncatedCell align="center" maxWidth={300}>
                                                <Tooltip title={nombreArchivo}>
                                                    <span>{nombreArchivo}</span>
                                                </Tooltip>
                                            </TruncatedCell>
                                            <TableCell style={{ maxWidth: 100, textAlign: 'center' }}>{formatearTamanio(doc.tamaño)}</TableCell>
                                            <TableCell style={{ maxWidth: 80, textAlign: 'center' }}>
                                                <Tooltip title="Descargar archivo">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={(e) => descargarArchivo(e, doc.id, nombreArchivo)}
                                                        size="small"
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                <PaginacionTabla totalPages={meta?.totalPages || 1} />
                <SelectNumeroCursos perPage={meta?.per_page || 10} />
            </Box>

            {/* Modal de Preview */}
            <Dialog
                open={modalAbierto}
                onClose={cerrarModal}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    style: {
                        height: '95vh'
                    }
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {documentoSeleccionado && obtenerNombreArchivo(documentoSeleccionado.ruta)}
                        </Typography>
                        <IconButton onClick={cerrarModal} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers style={{ padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {cargandoPreview ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                        </Box>
                    ) : errorPreview ? (
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
                            <Typography color="error" variant="h6">
                                Error al cargar el archivo
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mt={2}>
                                No se pudo cargar la vista previa del archivo
                            </Typography>
                        </Box>
                    ) : urlPreview && documentoSeleccionado ? (
                        (() => {
                            const tipoArchivo = obtenerTipoArchivo(obtenerNombreArchivo(documentoSeleccionado.ruta));
                            
                            if (tipoArchivo === 'pdf') {
                                return (
                                    <object
                                        data={urlPreview}
                                        type="application/pdf"
                                        style={{
                                            width: '100%',
                                            height: '70vh',
                                            border: 'none'
                                        }}
                                    >
                                        <embed
                                            src={urlPreview}
                                            type="application/pdf"
                                            style={{
                                                width: '100%',
                                                height: '70vh',
                                                border: 'none'
                                            }}
                                        />
                                    </object>
                                );
                            } else if (tipoArchivo === 'image') {
                                return (
                                    <Box p={2} display="flex" justifyContent="center" alignItems="center">
                                        <img
                                            src={urlPreview}
                                            alt="Preview"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '70vh',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Box>
                                );
                            } else if (tipoArchivo === 'video') {
                                return (
                                    <video
                                        src={urlPreview}
                                        controls
                                        style={{ maxWidth: '100%', maxHeight: '70vh' }}
                                    />
                                );
                            } else if (tipoArchivo === 'audio') {
                                return (
                                    <audio
                                        src={urlPreview}
                                        controls
                                        style={{ width: '100%' }}
                                    />
                                );
                            } else if (tipoArchivo === 'html') {
                                return (
                                    <iframe
                                        src={urlPreview}
                                        title="Preview HTML"
                                        style={{
                                            width: '100%',
                                            height: '70vh',
                                            border: 'none'
                                        }}
                                    />
                                );
                            } else if (tipoArchivo === 'text') {
                                return (
                                    <iframe
                                        src={urlPreview}
                                        title="Preview Text"
                                        style={{
                                            width: '100%',
                                            height: '70vh',
                                            border: 'none',
                                            backgroundColor: 'white'
                                        }}
                                    />
                                );
                            } else {
                                return (
                                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" p={4}>
                                        <Typography variant="h6" gutterBottom>
                                            Vista previa no disponible
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" align="center">
                                            Este tipo de archivo no se puede previsualizar. 
                                            Por favor, descárgalo para verlo.
                                        </Typography>
                                    </Box>
                                );
                            }
                        })()
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={(e) => {
                            if (documentoSeleccionado) {
                                descargarArchivo(e, documentoSeleccionado.id, obtenerNombreArchivo(documentoSeleccionado.ruta));
                            }
                        }}
                        color="primary"
                        variant="contained"
                        startIcon={<DownloadIcon />}
                    >
                        Descargar
                    </Button>
                    <Button onClick={cerrarModal} color="inherit">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TablaDocumentos;
